/**
 * ProcessingView - Shows document analysis progress with "Behind the magic curtains" details
 * Builds user confidence by showing what's happening during extraction
 */

import { useState, useEffect } from "react";
import {
  Loader2,
  FileText,
  Building2,
  CreditCard,
  Users,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Brain,
  Database,
  Search,
  Wand2,
  Expand,
  Fingerprint,
  BarChart3,
  Route,
  FileOutput,
  ShieldCheck,
  MapPin,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ProcessingCanvasView } from "./ProcessingCanvasView";
import type { SourceDocument } from "@shared/discovery-types";

interface ProcessingStep {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  detailSteps?: string[];
}

// Legacy processing steps (Anchor → Census → Holistic)
const PROCESSING_STEPS: ProcessingStep[] = [
  {
    id: "classify",
    label: "Classifying Documents",
    description: "Identifying document types",
    icon: FileText,
    detailSteps: [
      "Analyzing file formats and content structure",
      "Detecting bank statements, invoices, tax forms",
      "Assigning confidence scores to classifications",
    ],
  },
  {
    id: "extract",
    label: "Extracting Company Data",
    description: "Reading Swiss company information",
    icon: Building2,
    detailSteps: [
      "Scanning for UID (Unternehmens-Identifikationsnummer)",
      "Extracting company name and legal form",
      "Finding registered address and contact details",
      "Identifying fiscal year and VAT registration",
    ],
  },
  {
    id: "banking",
    label: "Processing Bank Data",
    description: "Parsing account information",
    icon: CreditCard,
    detailSteps: [
      "Detecting IBAN and account numbers",
      "Identifying bank names from Swiss clearing numbers",
      "Parsing transaction patterns and frequencies",
      "Calculating account balances and activity",
    ],
  },
  {
    id: "entities",
    label: "Identifying Vendors & Customers",
    description: "Building your business network",
    icon: Users,
    detailSteps: [
      "Analyzing payment recipients and senders",
      "Grouping transactions by counterparty",
      "Distinguishing vendors from customers by cash flow",
      "Detecting refunds and matching to vendors",
      "Categorizing business relationships",
    ],
  },
  {
    id: "profile",
    label: "Building Company Profile",
    description: "Assembling the complete picture",
    icon: Sparkles,
    detailSteps: [
      "Merging data from multiple documents",
      "Resolving conflicts between sources",
      "Calculating overall confidence scores",
      "Preparing preview for your review",
    ],
  },
];

// Census-First Pipeline steps (6-phase: DETECT → CENSUS → STRATEGY → EXTRACT → ATTRIBUTE → PLACE)
const CENSUS_FIRST_STEPS: ProcessingStep[] = [
  {
    id: "detect",
    label: "Detecting Documents",
    description: "Fingerprinting your files",
    icon: Fingerprint,
    detailSteps: [
      "Analyzing file structure and encoding",
      "Detecting document type and source institution",
      "Identifying date ranges and fiscal periods",
      "Finding account references and entities",
    ],
  },
  {
    id: "census",
    label: "Building Census",
    description: "Understanding your document corpus",
    icon: BarChart3,
    detailSteps: [
      "Mapping coverage by fiscal year",
      "Identifying definitive sources (year-end statements)",
      "Detecting coverage gaps",
      "Building document relationships",
    ],
  },
  {
    id: "strategy",
    label: "Planning Extraction",
    description: "Creating extraction strategy",
    icon: Route,
    detailSteps: [
      "Determining extraction priorities",
      "Planning validation rules",
      "Setting up cross-document validations",
      "Defining routing rules",
    ],
  },
  {
    id: "extract",
    label: "Extracting Facts",
    description: "Source-attributed extraction",
    icon: FileOutput,
    detailSteps: [
      "Parsing transactions with full provenance",
      "Extracting balances and amounts",
      "Identifying counterparties",
      "Capturing company information",
    ],
  },
  {
    id: "attribute",
    label: "Validating & Linking",
    description: "Cross-validation and entity linking",
    icon: ShieldCheck,
    detailSteps: [
      "Running self-validations per document",
      "Cross-validating between documents",
      "Detecting and resolving duplicates",
      "Linking entities across sources",
    ],
  },
  {
    id: "place",
    label: "Preparing Results",
    description: "Routing to system modules",
    icon: MapPin,
    detailSteps: [
      "Generating routing decisions",
      "Transforming records for import",
      "Creating audit trail entries",
      "Building quality assessment",
    ],
  },
];

interface ProcessingViewProps {
  /** Number of files being processed */
  fileCount?: number;
  /** Optional callback when user wants to see more details */
  onShowDetails?: () => void;
  /** Existing documents with their current status (if available) */
  existingDocuments?: SourceDocument[];
  /** Real progress from backend (tiered processing) */
  processingProgress?: {
    phase: string;
    phases: string[];
    percentComplete: number;
    message: string;
  };
  /** Processing phase from session (determines which pipeline mode to show) */
  processingPhase?: string;
  /** Callback to cancel processing */
  onCancel?: () => void;
  /** Display name of the AI model being used (e.g. "Gemini 3.0 Flash") */
  modelName?: string;
}

/**
 * Check if we're using Census-First pipeline based on processing phase
 */
function isCensusFirstPipeline(phase?: string): boolean {
  if (!phase) return false;
  return phase.startsWith("census_first");
}

/**
 * Get current step index for Census-First pipeline based on phase name
 */
function getCensusFirstStepIndex(phase?: string): number {
  if (!phase) return 0;

  // Map processing phases to step indices
  const phaseMapping: Record<string, number> = {
    census_first_running: 0, // Starting with DETECT
    census_first_detect: 0,
    census_first_census: 1,
    census_first_strategy: 2,
    census_first_extract: 3,
    census_first_attribute: 4,
    census_first_place: 5,
    census_first_complete: 5,
  };

  return phaseMapping[phase] ?? 0;
}

export function ProcessingView({
  fileCount = 0,
  existingDocuments = [],
  processingProgress,
  processingPhase,
  onCancel,
  modelName,
}: ProcessingViewProps) {
  // Determine which pipeline mode and steps to use
  const useCensusFirst = isCensusFirstPipeline(processingPhase);
  const steps = useCensusFirst ? CENSUS_FIRST_STEPS : PROCESSING_STEPS;

  // For Census-First, get initial step from phase; for legacy, start at 0
  const initialStepIndex = useCensusFirst
    ? getCensusFirstStepIndex(processingPhase)
    : 0;

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(() => {
    // For Census-First, mark previous steps as completed based on current phase
    if (useCensusFirst) {
      const completed = new Set<string>();
      for (let i = 0; i < initialStepIndex; i++) {
        completed.add(CENSUS_FIRST_STEPS[i].id);
      }
      return completed;
    }
    return new Set();
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentDetailStep, setCurrentDetailStep] = useState(0);
  const [showCanvas, setShowCanvas] = useState(false);

  // Update step index when processingPhase changes (for Census-First real-time updates)
  useEffect(() => {
    if (useCensusFirst && processingPhase) {
      const newIndex = getCensusFirstStepIndex(processingPhase);
      if (newIndex > currentStepIndex) {
        // Mark previous steps as completed
        setCompletedSteps(prev => {
          const newCompleted = new Set(prev);
          for (let i = 0; i < newIndex; i++) {
            newCompleted.add(CENSUS_FIRST_STEPS[i].id);
          }
          return newCompleted;
        });
        setCurrentStepIndex(newIndex);
        setCurrentDetailStep(0);
      }
    }
  }, [processingPhase, useCensusFirst, currentStepIndex]);

  // Simulate progress through steps (only for legacy mode or when no real progress)
  useEffect(() => {
    // Don't simulate if we have real Census-First progress
    if (
      useCensusFirst &&
      processingPhase &&
      processingPhase !== "census_first_running"
    ) {
      return;
    }

    const stepDuration = 3000; // Base duration per step
    const detailDuration = 800; // Duration per detail step

    const stepTimer = setInterval(() => {
      setCurrentStepIndex(prev => {
        if (prev < steps.length - 1) {
          // Mark current step as completed
          setCompletedSteps(completed => {
            const newCompleted = new Set(completed);
            newCompleted.add(steps[prev].id);
            return newCompleted;
          });
          return prev + 1;
        }
        return prev;
      });
      setCurrentDetailStep(0);
    }, stepDuration);

    const detailTimer = setInterval(() => {
      setCurrentDetailStep(prev => {
        const step = steps[currentStepIndex];
        const maxDetails = step?.detailSteps?.length || 0;
        if (prev < maxDetails - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, detailDuration);

    return () => {
      clearInterval(stepTimer);
      clearInterval(detailTimer);
    };
  }, [currentStepIndex, useCensusFirst, processingPhase, steps]);

  const currentStep = steps[currentStepIndex];

  return (
    <Card className="max-w-lg mx-auto overflow-hidden">
      <CardContent className="py-8">
        {/* Main Progress Indicator */}
        <div className="text-center mb-8">
          <div className="relative inline-flex items-center justify-center mb-6">
            {/* Animated rings */}
            <div
              className="absolute inset-0 animate-ping rounded-full bg-primary/20"
              style={{ animationDuration: "2s" }}
            />
            <div className="absolute inset-2 animate-pulse rounded-full bg-primary/10" />
            <div className="relative h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <currentStep.icon className="h-10 w-10 text-primary animate-pulse" />
            </div>
          </div>
          <h3 className="text-xl font-semibold mb-1">{currentStep.label}</h3>
          <p className="text-muted-foreground text-sm">
            {currentStep.description}
          </p>
          {fileCount > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Processing {fileCount} document{fileCount !== 1 ? "s" : ""}
            </p>
          )}

          {/* Real Progress from Backend */}
          {processingProgress && (
            <div className="mt-4 space-y-2">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${processingProgress.percentComplete}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {processingProgress.message}
                {processingProgress.percentComplete > 0 &&
                  ` (${processingProgress.percentComplete}%)`}
              </p>
            </div>
          )}
        </div>

        {/* Cancel Button */}
        {onCancel && (
          <div className="flex justify-center mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        )}

        {/* Step Progress Bar */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(step.id);
            const isCurrent = index === currentStepIndex;
            const StepIcon = step.icon;

            return (
              <div
                key={step.id}
                className={cn(
                  "flex items-center justify-center h-8 w-8 rounded-full transition-all duration-500",
                  isCompleted
                    ? "bg-[var(--success)] text-[var(--success-foreground)]"
                    : isCurrent
                      ? "bg-primary text-primary-foreground scale-110"
                      : "bg-muted text-muted-foreground"
                )}
                title={step.label}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <StepIcon
                    className={cn("h-4 w-4", isCurrent && "animate-pulse")}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Pipeline Mode Indicator */}
        {useCensusFirst && (
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
              <Fingerprint className="h-3 w-3" />
              Census-First Pipeline
            </span>
          </div>
        )}

        {/* Behind the Magic Curtains - Expandable */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer py-2">
              <Wand2 className="h-4 w-4" />
              <span>Behind the magic curtains</span>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            <div className="mt-4 pt-4 border-t border-border/50">
              {/* Current Step Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <Brain className="h-3.5 w-3.5" />
                  What's happening right now
                </div>

                <div className="space-y-2 pl-1">
                  {currentStep.detailSteps?.map((detail, index) => {
                    const isActive = index === currentDetailStep;
                    const isDone = index < currentDetailStep;

                    return (
                      <div
                        key={index}
                        className={cn(
                          "flex items-start gap-2 text-sm transition-all duration-300",
                          isActive
                            ? "text-foreground"
                            : isDone
                              ? "text-muted-foreground"
                              : "text-muted-foreground/50"
                        )}
                      >
                        <div className="mt-0.5 flex-shrink-0">
                          {isDone ? (
                            <Check className="h-3.5 w-3.5 text-[var(--success)]" />
                          ) : isActive ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                          ) : (
                            <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30" />
                          )}
                        </div>
                        <span className={cn(isActive && "font-medium")}>
                          {detail}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tech Stack Info */}
              <div className="mt-6 pt-4 border-t border-border/30">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                  <Database className="h-3.5 w-3.5" />
                  Technology at work
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    <span>{modelName ?? "Gemini AI"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="h-2 w-2 rounded-full bg-[var(--success)]" />
                    <span>Swiss bank parsers</span>
                  </div>
                  {useCensusFirst ? (
                    <>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-[var(--warning)]" />
                        <span>Census-First pipeline</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-primary/60" />
                        <span>Source attribution</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-[var(--warning)]" />
                        <span>Holistic extraction</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-primary/60" />
                        <span>Entity recognition</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Data Location & Compliance */}
              <div className="mt-4 pt-4 border-t border-border/30">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                  <MapPin className="h-3.5 w-3.5" />
                  Data Location & Compliance
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Region</span>
                    <span className="font-medium text-foreground">
                      Zurich, Switzerland
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Datacenter</span>
                    <span className="font-mono">europe-west6</span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>FINMA Compliance</span>
                    <span className="inline-flex items-center gap-1 text-[var(--success)]">
                      <ShieldCheck className="h-3 w-3" />
                      Compliant
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Data Residency</span>
                    <span className="font-medium text-foreground">
                      🇨🇭 Switzerland
                    </span>
                  </div>
                </div>
              </div>

              {/* Fun Fact */}
              <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
                <div className="flex items-start gap-2">
                  <Search className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Did you know?
                    </span>{" "}
                    {useCensusFirst
                      ? "Our Census-First pipeline builds a complete understanding of your documents BEFORE extracting data. Every fact is traced back to its source for full auditability."
                      : "Our holistic extractor reads all your documents together to build a unified understanding of your business, resolving conflicts and filling gaps automatically."}
                  </p>
                </div>
              </div>

              {/* Expand to Canvas View */}
              <div className="mt-4 pt-3 border-t border-border/30">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCanvas(true)}
                  className="w-full gap-2"
                >
                  <Expand className="h-4 w-4" />
                  Open Discovery Canvas
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>

      {/* Full-screen Canvas View */}
      <ProcessingCanvasView
        isOpen={showCanvas}
        onClose={() => setShowCanvas(false)}
        fileCount={fileCount}
        existingDocuments={existingDocuments}
        modelName={modelName}
      />
    </Card>
  );
}
